import { ReactNode } from 'react'
import { Button, EmissionsSummaryPanelModule, TabMenu } from '../../components/BaseComponents'
import { CarbonAccountSummary } from '../../types/CarbonAccountSummary'
import { getCurrencySymbol } from '../../lib/utils'
import useCurrency from '../../hooks/useCurrency'

export type ProjectsListZones = {
	summary: ReactNode
	tabs: ReactNode
	actions: ReactNode
	table: ReactNode
}

type UseProjectsListZonesArgs = {
	onCreateClick: () => void
	tableComponent: ReactNode
	isLoading: boolean
	projectSummary: any
	summaryError?: any
	scope: string
	onScopeChange: (scope: string) => void
}

export function useProjectsListZones({
	onCreateClick,
	tableComponent,
	isLoading,
	projectSummary,
	summaryError,
	scope,
	onScopeChange,
}: UseProjectsListZonesArgs): ProjectsListZones {
	const currency = useCurrency()

	const errorText = (error: any): string | undefined => {
		if (error === undefined || error === null) {
			return undefined
		}
		if (typeof error === 'string') {
			return error
		}
		return error.message ?? String(error)
	}

	return {
		summary: (
			<EmissionsSummaryPanelModule
				carbonSummary={{
					intensityRatio: String(projectSummary?.intensity ?? 0),
					localIntensityRatio: String(projectSummary?.intensity ?? 0),
					scope1Total: String(projectSummary?.scope1Total ?? 0),
					scope2Total: String(projectSummary?.scope2Total ?? 0),
					scope3Total: String(projectSummary?.scope3Total ?? 0),
					scope1Completion: String(projectSummary?.scope1Completion ?? 0),
					scope2Completion: String(projectSummary?.scope2Completion ?? 0),
					scope3Completion: String(projectSummary?.scope3Completion ?? 0),
					totalCompletion: String(projectSummary?.totalCompletion ?? 0),
				} as CarbonAccountSummary}
				totalEmissions={projectSummary?.totalEmissions ?? 0}
				intensityRatioUnit={`${getCurrencySymbol(currency)} of all projects value`}
				activeScope={scope as '' | '1' | '2' | '3'}
				loading={isLoading && !projectSummary}
				error={errorText(summaryError)}
			/>
		),
		tabs: (
			<TabMenu
				tabs={[
					{ id: '', label: 'Overview' },
					{ id: '1', label: 'Scope 1' },
					{ id: '2', label: 'Scope 2' },
					{ id: '3', label: 'Scope 3 (upstream)' },
				]}
				value={scope}
				onChange={onScopeChange}
				ariaLabel='Emissions scope'
				testId='scope-tabs'
				additionalClass='hidden sm:block'
			/>
		),
		actions: (
			<Button
				requiresWrite
				type='Primary'
				text='Create project'
				hasTrailingIcon
				icon='fa-solid fa-plus'
				size='sm'
				onClick={onCreateClick}
			/>
		),
		table: tableComponent,
	}
}
